// src/pages/CompanyDefaults/hooks/useCompanyDefaultsModal.js
import { useState, useCallback } from "react"; // Added useCallback
import API_BASE_URL from "../../../../../Constants";
import { defaultCompanyDefaultsFormData } from "./companyDefaultsDataConfig";

export default function useCompanyDefaultsModal() {
  const [formData, setFormData] = useState(defaultCompanyDefaultsFormData);
  const [isEditing, setIsEditing] = useState(false);
  const [id, setId] = useState(null); // Start with null ID, load #1 via effect in parent if needed
  const [companyList, setCompanyList] = useState([]);
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  const BASE_URL = `${API_BASE_URL}/api/setup/companydefaults`;

  // --- Fetch the list of all companies ---
  // Use useCallback to stabilize the function reference for useEffect dependencies
  const fetchCompanyList = useCallback(async () => {
    console.log("Fetching company list...");
    setIsLoadingList(true);
    setCompanyList([]);
    try {
      const response = await fetch(BASE_URL, { credentials: "include" });
      if (!response.ok) {
        throw new Error(`Failed to fetch company list (${response.status})`);
      }
      const list = await response.json();
      console.log("Received company list:", list);
      setCompanyList(list || []);
    } catch (error) {
      console.error("Error fetching company list:", error);
      setCompanyList([]);
      alert(`Error fetching company list: ${error.message}`); // Replace with Snackbar if preferred
    } finally {
      setIsLoadingList(false);
    }
  }, [BASE_URL]); // Dependency: BASE_URL (usually constant)

  // --- Fetch details for a specific company ---
  const handleLookup = useCallback(async (lookupId) => {
    if (lookupId === null || lookupId === undefined) {
      console.log("handleLookup skipped: No lookup ID provided.");
      return;
    }
    console.log(`Looking up details for ID: ${lookupId}...`);
    setIsLoadingDetails(true);
    setIsEditing(false); // Always exit edit mode when loading
    try {
      const response = await fetch(`${BASE_URL}/${lookupId}`, { credentials: "include" });
      if (!response.ok) {
        throw new Error(`Record not found (ID: ${lookupId}, Status: ${response.status})`);
      }
      const data = await response.json();
      console.log(`Loaded record ${lookupId} from server:`, data);
      setFormData(data);
      setId(lookupId); // Update the current ID state
    } catch (error) {
      console.error(error);
      alert(`Error fetching record #${lookupId}: ${error.message}`); // Replace with Snackbar
      // Decide how to handle failed lookup - reset form?
      setFormData(defaultCompanyDefaultsFormData);
      setId(null);
    } finally {
      setIsLoadingDetails(false);
    }
  }, [BASE_URL]); // Dependency: BASE_URL

  // Prepare for creating a NEW item
  const prepareNewItem = useCallback(() => {
    console.log("Preparing for new item creation...");
    setId(null);
    setFormData(defaultCompanyDefaultsFormData);
    setIsEditing(true);
  }, []); // No dependencies

  // POST (Create New Item)
  const createItem = useCallback(async (newData) => {
    console.log("Attempting POST to URL:", BASE_URL);
    console.log("Data being sent (createItem):", JSON.stringify(newData, null, 2));
    try {
      const response = await fetch(`${BASE_URL}`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newData),
      });
      if (!response.ok) {
        const errorBody = await response.text();
        console.error("POST request failed:", response.status, errorBody);
        let errorMessage = `Failed to create item. Status: ${response.status}.`;
         try { const parsedError = JSON.parse(errorBody); errorMessage = parsedError.message || errorMessage + ` ${errorBody}`; } catch(e) { errorMessage += ` ${errorBody}`; }
        throw new Error(errorMessage);
      }
      const createdItem = await response.json();
      console.log("Successfully created item:", createdItem);
      // NOTE: Success alert/snackbar should be handled by the component calling this
      return createdItem; // Return the created item
    } catch (error) {
      console.error("Create Error:", error);
      throw error; // Re-throw error to be caught by calling component
    }
  }, [BASE_URL]); // Dependency: BASE_URL

  // PUT by ID (Update Existing Item)
  const editItem = useCallback(async (updatedData) => {
    const currentId = id; // Capture id at time of call
    if (currentId === null) {
      console.error("editItem skipped: ID is null.");
      throw new Error("Cannot update item without an ID.");
    }
    console.log(`editItem => Doing PUT for ID ${currentId} with: `, updatedData);
    try {
      const response = await fetch(`${BASE_URL}/${currentId}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updatedData),
      });
      if (!response.ok) {
        const errorBody = await response.text();
        console.error("PUT request failed:", response.status, errorBody);
        let errorMessage = `Failed to update item ${currentId}. Status: ${response.status}.`;
         try { const parsedError = JSON.parse(errorBody); errorMessage = parsedError.message || errorMessage + ` ${errorBody}`; } catch(e) { errorMessage += ` ${errorBody}`; }
        throw new Error(errorMessage);
      }
      console.log(`Successfully updated item ${currentId}`);
      // NOTE: Success alert/snackbar should be handled by the component calling this
      // Optionally fetch updated data if needed, or assume local data is fine
      // const updatedFromServer = await response.json(); // If backend returns updated obj
      // setFormData(updatedFromServer); // Update local state
    } catch (error) {
      console.error("Edit Error:", error);
      throw error; // Re-throw error
    }
  }, [id, BASE_URL]); // Dependency: id, BASE_URL

  // DELETE by ID
  const handleDelete = useCallback(async () => {
     const currentId = id; // Capture id at time of call
     if (currentId === null) {
        console.error("handleDelete skipped: ID is null.");
        throw new Error("Cannot delete item without an ID.");
    }
    console.log(`handleDelete => Doing DELETE for ID ${currentId}`);
    try {
      const response = await fetch(`${BASE_URL}/${currentId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!response.ok) {
        const errorBody = await response.text();
        console.error("DELETE request failed:", response.status, errorBody);
        let errorMessage = `Failed to delete item ${currentId}. Status: ${response.status}.`;
         try { const parsedError = JSON.parse(errorBody); errorMessage = parsedError.message || errorMessage + ` ${errorBody}`; } catch(e) { errorMessage += ` ${errorBody}`; }
        throw new Error(errorMessage);
      }
      console.log(`Successfully deleted item ${currentId}`);
      // Reset form and ID after successful delete
      setFormData(defaultCompanyDefaultsFormData);
      setId(null); // Set ID to null after delete
      setIsEditing(false);
       // NOTE: Success alert/snackbar should be handled by the component calling this
    } catch (error) {
      console.error("Delete Error:", error);
      throw error; // Re-throw error
    }
  }, [id, BASE_URL]); // Dependency: id, BASE_URL

  // Field change handler (passed down)
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []); // No dependencies needed if only using setFormData updater form

  return {
    formData,
    setFormData,
    isEditing,
    setIsEditing,
    id,
    setId, // Expose setId in case parent needs it after creation
    handleLookup,
    prepareNewItem,
    createItem,
    editItem,
    handleDelete,
    handleChange,
    companyList,
    fetchCompanyList,
    isLoadingList,
    isLoadingDetails,
  };
}